import { useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Copy,
  CreditCard,
  FileText,
  Loader2,
  PenLine,
  Printer,
  ReceiptText,
  X,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ImageDialog } from '@/components/ui/image-dialog';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  formatCurrency,
  formatDateTime,
  formatNumber,
  formatPhone,
} from '@/lib/format';
import { cn } from '@/lib/utils';
import { usePermission } from '@/features/auth/auth.store';
import { paymentLabel, statusMeta, typeLabelKey } from './order-enums';
import {
  useGiftCardBalance,
  usePaymentLogs,
  useReceiptItems,
} from './orders.api';
import type { Order, OrderItem } from './types';

/**
 * The order receipt.
 *
 * Same content as the original's `OrderDetailsModal`: service, POS block,
 * location, the items split into included and voided, and the payment
 * breakdown. Rendered as one scrollable sheet so it prints as a receipt rather
 * than as a modal with a nested table.
 */

const TYPE_IN_STORE = 5;

function Line({
  label,
  value,
  strong,
}: {
  label: string;
  value: React.ReactNode;
  strong?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={cn('text-right', strong && 'font-semibold tabular-nums')}>
        {value}
      </dd>
    </div>
  );
}

function ItemLines({ items }: { items: OrderItem[] }) {
  return (
    <ul className="space-y-1.5">
      {items.map((item) => (
        <li key={item.id} className="flex items-baseline justify-between gap-3 text-sm">
          <span className="min-w-0">
            <span className="text-muted-foreground mr-1.5 tabular-nums">
              {formatNumber(item.amount)}×
            </span>
            <span>{item.name || item.completeName || '—'}</span>
            {item.brand && (
              <span className="text-muted-foreground text-xs"> · {item.brand}</span>
            )}
          </span>
          <span className="shrink-0 tabular-nums">
            {formatCurrency(item.extendedPrice ?? item.price)}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * `pos/orders/payment/logs/{id}` — the gateway's own record of the transaction.
 * Admin only, as in the original, and only fetched once the panel is opened.
 */
function PaymentLogsPanel({ orderId }: { orderId: string }) {
  const { t } = useTranslation();
  const { data, isPending, isError } = usePaymentLogs(orderId, true);
  const [preview, setPreview] = useState<string | null>(null);

  if (isPending) {
    return (
      <div className="divide-y rounded-lg border">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="flex items-center gap-4 p-2.5">
            <Skeleton className="h-3.5 w-24" />
            <Skeleton className="h-3.5 flex-1" />
            <Skeleton className="h-3.5 w-16" />
          </div>
        ))}
      </div>
    );
  }

  if (isError || !data?.length) {
    return (
      <p className="text-muted-foreground py-4 text-center text-xs">
        {t('orders.noPaymentLogs')}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>{t('orders.createdOn')}</TableHead>
              <TableHead>{t('orders.operation')}</TableHead>
              <TableHead>{t('orders.terminalId')}</TableHead>
              <TableHead className="text-right">JSON</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((log) => (
              <TableRow key={log.id}>
                <TableCell className="text-xs whitespace-nowrap">
                  {formatDateTime(log.createdAt)}
                </TableCell>
                <TableCell className="text-xs">
                  {log.operationName || '—'}
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {log.terminalId || '—'}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="outline"
                      size="icon-sm"
                      aria-label={t('orders.preview')}
                      disabled={!log.jsonData}
                      onClick={() =>
                        setPreview((current) =>
                          current === log.jsonData ? null : log.jsonData
                        )
                      }
                    >
                      <FileText className="size-3.5" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon-sm"
                      aria-label={t('common.copy')}
                      disabled={!log.jsonData}
                      onClick={() =>
                        void navigator.clipboard
                          ?.writeText(log.jsonData ?? '')
                          .catch(() => undefined)
                      }
                    >
                      <Copy className="size-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {preview && (
        <pre className="bg-muted max-h-56 overflow-auto rounded-lg p-3 text-xs">
          {preview}
        </pre>
      )}
    </div>
  );
}

export function OrderReceiptDialog({
  order,
  open,
  onOpenChange,
}: {
  order: Order | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const isAdmin = usePermission('isSuperAdmin');
  const { data: items, isLoading } = useReceiptItems(
    open ? order?.id ?? null : null
  );
  const giftCard = useGiftCardBalance();

  const [showLogs, setShowLogs] = useState(false);
  const [showTerminal, setShowTerminal] = useState(false);
  const [image, setImage] = useState<{ src: string; title: string } | null>(
    null
  );

  if (!order) return null;

  const hasTerminalData =
    Array.isArray(order.terminalData) && order.terminalData.length > 0;

  /** The card nickname when one was used, otherwise the method's own name. */
  const paymentName =
    order.nickName || order.paymentName || paymentLabel(order.paymentType);

  const included = (items ?? []).filter((item) => !item.isVoided);
  const voided = (items ?? []).filter((item) => item.isVoided);
  const location = order.customerLocation;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          data-print-overlay
          className={cn(
            'fixed inset-0 z-50 bg-black/45 backdrop-blur-[2px]',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0'
          )}
        />
        <DialogPrimitive.Content
          data-print-root
          className={cn(
            'bg-background fixed top-1/2 left-1/2 z-50 flex max-h-[88svh] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 flex-col rounded-xl border shadow-xl',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95'
          )}
        >
          <div className="flex items-start justify-between gap-4 border-b p-4">
            <div>
              <DialogPrimitive.Title className="font-semibold">
                {t('orders.receipt')}
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="text-muted-foreground font-mono text-xs">
                {order.confirmationCode ?? ''}
              </DialogPrimitive.Description>
            </div>
            <div className="flex items-center gap-1" data-print-hide>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t('orders.print')}
                onClick={() => window.print()}
              >
                <Printer className="size-4" />
              </Button>
              <DialogPrimitive.Close className="text-muted-foreground hover:text-foreground focus-visible:ring-ring rounded p-1 focus-visible:ring-2 focus-visible:outline-none">
                <X className="size-4" />
                <span className="sr-only">{t('orders.close')}</span>
              </DialogPrimitive.Close>
            </div>
          </div>

          <div className="space-y-5 overflow-y-auto p-4" data-print-body>
            <section className="space-y-1.5">
              <h4 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                {t('orders.service')}
              </h4>
              <dl className="space-y-1.5">
                <Line
                  label={t('orders.typeColumn')}
                  value={t(typeLabelKey(order.type))}
                />
                <Line
                  label={t('orders.statusColumn')}
                  value={
                    <Badge variant={statusMeta(order.status).tone}>
                      {t(statusMeta(order.status).labelKey)}
                    </Badge>
                  }
                />
                <Line
                  label={t('orders.customer')}
                  value={order.customerName || '—'}
                />
                <Line
                  label={t('orders.contact')}
                  value={formatPhone(order.customerPhone)}
                />
                <Line
                  label={t('orders.date')}
                  value={formatDateTime(order.actualTime)}
                />
                <Line
                  label={t('orders.store')}
                  value={order.businessName || '—'}
                />
              </dl>
            </section>

            {order.type === TYPE_IN_STORE && (
              <>
                <Separator />
                <section className="space-y-1.5">
                  <h4 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                    POS
                  </h4>
                  <dl className="space-y-1.5">
                    <Line
                      label={t('orders.posLane')}
                      value={order.posLaneTag || '—'}
                    />
                    <Line
                      label={t('orders.terminalId')}
                      value={order.posTerminalId || '—'}
                    />
                    <Line
                      label={t('orders.version')}
                      value={order.posVersion || '—'}
                    />
                  </dl>
                </section>
              </>
            )}

            {(location || order.pickUpName || order.deliveryZoneName) && (
              <>
                <Separator />
                <section className="space-y-1.5">
                  <h4 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                    {t('orders.location')}
                  </h4>
                  {location && (
                    <p className="text-sm">
                      {location.name && (
                        <span className="block font-medium">{location.name}</span>
                      )}
                      {location.addressLine}
                      <span className="text-muted-foreground block">
                        {[location.city, location.zipcode]
                          .filter(Boolean)
                          .join(', ')}
                      </span>
                    </p>
                  )}
                  {location?.instructions && (
                    <p className="text-muted-foreground text-xs italic">
                      {location.instructions}
                    </p>
                  )}
                  <dl className="space-y-1.5">
                    {order.deliveryZoneName && (
                      <Line
                        label={t('orders.zone')}
                        value={order.deliveryZoneName}
                      />
                    )}
                    {order.pickUpName && (
                      <Line
                        label={t('orders.pickupName')}
                        value={order.pickUpName}
                      />
                    )}
                  </dl>
                </section>
              </>
            )}

            <Separator data-print-hide />

            {/* Same four controls as the original, in the same disabled-when-
                absent state — an unavailable document reads as "this order has
                none", not as a missing feature. */}
            <section className="space-y-2" data-print-hide>
              <h4 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                {t('orders.documents')}
              </h4>
              <div className="flex flex-wrap gap-2">
                {isAdmin && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowLogs((value) => !value)}
                  >
                    <ReceiptText className="size-3.5" />
                    {t('orders.paymentLogs')}
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!hasTerminalData}
                  onClick={() => setShowTerminal((value) => !value)}
                >
                  <FileText className="size-3.5" />
                  {t('orders.terminalData')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!order.signatureImageUrl}
                  onClick={() =>
                    setImage({
                      src: order.signatureImageUrl!,
                      title: t('orders.signature'),
                    })
                  }
                >
                  <PenLine className="size-3.5" />
                  {t('orders.signature')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!order.ebtReceiptUrl}
                  onClick={() =>
                    setImage({
                      src: order.ebtReceiptUrl!,
                      title: t('orders.ebtReceipt'),
                    })
                  }
                >
                  <FileText className="size-3.5" />
                  EBT
                </Button>
              </div>

              {showLogs && isAdmin && <PaymentLogsPanel orderId={order.id} />}

              {showTerminal && hasTerminalData && (
                <pre className="bg-muted max-h-56 overflow-auto rounded-lg p-3 text-xs">
                  {JSON.stringify(order.terminalData, null, 2)}
                </pre>
              )}
            </section>

            <Separator />

            <section className="space-y-2">
              <h4 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                {t('orders.itemsIncluded', { count: included.length })}
              </h4>
              {isLoading ? (
                <ul className="space-y-1.5">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <li key={index} className="flex justify-between gap-3">
                      <Skeleton className="h-4 w-2/3" />
                      <Skeleton className="h-4 w-14" />
                    </li>
                  ))}
                </ul>
              ) : included.length ? (
                <ItemLines items={included} />
              ) : (
                <p className="text-muted-foreground text-sm">
                  {t('orders.noItems')}
                </p>
              )}
            </section>

            {voided.length > 0 && (
              <section className="space-y-2 opacity-60">
                <h4 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                  {t('orders.itemsNotIncluded', { count: voided.length })}
                </h4>
                <ItemLines items={voided} />
              </section>
            )}

            <Separator />

            <section className="space-y-1.5">
              <h4 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                {t('orders.paymentInformation')}
              </h4>
              {/* The method, named. A card shows its nickname; anything else
                  shows the method's own name, with its logo where the API
                  supplies one. */}
              <div className="flex items-center gap-2 pb-1">
                {order.paymentImageUrl ? (
                  <img
                    src={order.paymentImageUrl}
                    alt=""
                    className="h-4 w-auto"
                  />
                ) : (
                  <CreditCard className="text-muted-foreground size-4" />
                )}
                <span className="text-sm font-medium">{paymentName}</span>
                {order.brand && (
                  <span className="text-muted-foreground text-xs uppercase">
                    {order.brand}
                  </span>
                )}
              </div>

              <dl className="space-y-1.5">
                <Line
                  label={t('orders.subtotal')}
                  // The original prefers a pre-discount subtotal when present.
                  value={formatCurrency(
                    order.originalSubtotal ?? order.productsTotal
                  )}
                />
                <Line
                  label={t('orders.discount')}
                  value={
                    <span className="text-destructive">
                      {formatCurrency(order.discountAmount)}
                    </span>
                  }
                />
                {order.giftCardCode && (
                  <div className="flex items-baseline justify-between gap-4 text-sm">
                    <dt className="text-muted-foreground flex items-center gap-1.5">
                      {t('orders.giftCard')}
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-6 font-mono text-xs"
                        disabled={giftCard.isPending}
                        onClick={() =>
                          giftCard.mutate(order.giftCardCode!, {
                            onSuccess: (balance) =>
                              toast.success(
                                t('orders.giftCardBalance', {
                                  amount: formatCurrency(
                                    balance?.balance ?? balance?.amount ?? 0
                                  ),
                                })
                              ),
                            onError: () =>
                              toast.error(t('orders.giftCardFailed')),
                          })
                        }
                      >
                        {giftCard.isPending ? (
                          <Loader2 className="size-3 animate-spin" />
                        ) : (
                          order.giftCardCode
                        )}
                      </Button>
                    </dt>
                    <dd className="text-destructive text-right tabular-nums">
                      {formatCurrency(order.giftCardValue)}
                    </dd>
                  </div>
                )}
                <Line
                  label={t('orders.municipalTax')}
                  value={formatCurrency(order.municipalTax)}
                />
                <Line
                  label={t('orders.stateTax')}
                  value={formatCurrency(order.stateTax)}
                />
                <Line
                  // One combined line, as the original: processing fee plus
                  // delivery, not two separate rows.
                  label={t('orders.serviceCost')}
                  value={formatCurrency(
                    (order.processingFee ?? 0) + (order.deliveryTotal ?? 0)
                  )}
                />
                <Separator className="my-1" />
                <Line
                  label={t('orders.total')}
                  value={formatCurrency(order.orderTotal)}
                  strong
                />
              </dl>
            </section>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>

      <ImageDialog
        src={image?.src ?? null}
        title={image?.title ?? ''}
        open={image !== null}
        onOpenChange={(next) => {
          if (!next) setImage(null);
        }}
      />
    </DialogPrimitive.Root>
  );
}
